import React from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, ArrowLeft, ShieldCheck, Zap, BadgeCheck, Sparkles } from "lucide-react";
import { HERO_IMAGE, WHATSAPP_LINK, BRAND } from "@/lib/constants";

export default function Hero() {
  return (
    <section
      className="relative overflow-hidden pt-28 md:pt-32 pb-20 md:pb-28"
      data-testid="hero-section"
      style={{
        background:
          "radial-gradient(1000px 600px at 85% 10%, rgba(22,90,68,0.85), transparent 60%), radial-gradient(900px 500px at 10% 90%, rgba(201,163,85,0.18), transparent 65%), linear-gradient(180deg, #0B3125 0%, #0F3D2E 55%, #08281E 100%)",
      }}
    >
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #C9A355 1px, transparent 0)",
          backgroundSize: "34px 34px",
        }}
      />
      <div className="grain-overlay" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Right side in RTL = content */}
          <div className="text-white order-2 lg:order-1 text-right fade-up">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold/15 border border-brand-gold/40 backdrop-blur-sm mb-4"
              data-testid="hero-badge"
            >
              <BadgeCheck className="w-4 h-4 text-brand-gold" />
              <span className="text-sm font-bold text-brand-gold-light">
                تم توفير الدفعة الأولى نيابة عن العميل
              </span>
            </div>

            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm mb-6 ms-0 md:ms-2" data-testid="hero-moc-badge">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-green-400" fill="currentColor">
                <path d="M12 2L4 6v6c0 5 3.5 9.5 8 10 4.5-.5 8-5 8-10V6l-8-4zm-1 15l-5-5 1.4-1.4L11 14.2l6.6-6.6L19 9l-8 8z"/>
              </svg>
              <span className="text-xs font-bold text-white/90">
                موثّق من وزارة التجارة · المملكة العربية السعودية
              </span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.15] tracking-tight">
              احصل على{" "}
              <span className="text-gold-gradient">سيولة مالية فورية</span>
              <br className="hidden sm:block" /> خلال دقائق
            </h1>

            <p className="mt-5 text-lg md:text-xl text-white/85 leading-relaxed max-w-xl">
              من <span className="text-brand-gold-light font-bold">1,000</span> إلى{" "}
              <span className="text-brand-gold-light font-bold">25,000 ريال</span>{" "}
              عبر <span className="font-bold">تابي</span> و{" "}
              <span className="font-bold">تمارا</span> — بدون تعقيد أو انتظار.
            </p>

            {/* Feature chips */}
            <div className="mt-7 flex flex-wrap gap-2.5">
              {[
                { icon: Zap, label: "تنفيذ خلال دقائق" },
                { icon: ShieldCheck, label: "آمن وموثوق 100%" },
                { icon: Sparkles, label: "متوافق شرعياً" },
              ].map((f, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/7 border border-white/10 text-sm font-semibold text-white/90 backdrop-blur-sm"
                >
                  <f.icon className="w-4 h-4 text-brand-gold-light" />
                  {f.label}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                data-testid="hero-cta-whatsapp"
                className="bg-[#25D366] hover:bg-[#1fb858] text-white rounded-full h-14 px-8 text-base font-extrabold gap-2 shadow-[0_10px_30px_-10px_rgba(37,211,102,0.6)] hover:scale-[1.03] transition-transform"
              >
                <a href={WHATSAPP_LINK()} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5" />
                  اطلب عبر واتساب
                  <ArrowLeft className="w-5 h-5 ms-1" />
                </a>
              </Button>

              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="hero-cta-call"
                className="bg-transparent border-brand-gold/40 hover:bg-brand-gold/10 text-white rounded-full h-14 px-8 text-base font-bold gap-2"
              >
                <a href={`tel:${BRAND.phone}`}>
                  اتصل الآن
                </a>
              </Button>
            </div>

            {/* Mini stats */}
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {[
                { v: "+500", l: "عميل راضٍ" },
                { v: "5 دقائق", l: "متوسط التنفيذ" },
                { v: "100%", l: "سرية وأمان" },
              ].map((s, i) => (
                <div key={i} className="border-s border-white/15 ps-4 first:border-s-0 first:ps-0">
                  <div className="font-heading text-2xl md:text-3xl font-extrabold text-gold-gradient">
                    {s.v}
                  </div>
                  <div className="text-xs md:text-sm text-white/70 mt-0.5">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Left in RTL = Image */}
          <div className="order-1 lg:order-2 relative fade-up-delay-2">
            <div className="relative max-w-[520px] mx-auto">
              {/* Gold glow */}
              <div className="absolute -inset-8 bg-brand-gold/20 blur-3xl rounded-full" />
              <div className="relative rounded-[28px] overflow-hidden ring-1 ring-brand-gold/30 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]">
                <img
                  src={HERO_IMAGE}
                  alt="عروض السيولة - ميرال كروب"
                  className="w-full h-auto object-cover"
                  data-testid="hero-image"
                  loading="eager"
                />
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-4 -start-4 sm:-start-6 bg-white rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3 border border-brand-gold/30">
                <div className="w-10 h-10 rounded-full bg-brand-green flex items-center justify-center">
                  <Zap className="w-5 h-5 text-brand-gold" />
                </div>
                <div>
                  <div className="text-xs text-brand-ink/60 font-semibold">تنفيذ فوري</div>
                  <div className="font-heading font-extrabold text-brand-green text-sm">خلال دقائق</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
